import React, { FC, useState } from "react";
import { Box } from "@mui/material";
import classNames from "classnames";
import { Button } from "../../Button/Button";
import { BRIEF_NOTE_MAX_LENGTH } from "./useBriefNotes";
import { MentionTextarea } from "../../Mentions/MentionTextarea";
import { TaggableUser, serializeMentions } from "../../Mentions/MentionText";
import { briefNoteTaggableUsers } from "../../../api/endpoints";

interface Props {
    saving: boolean;
    /** The thread is still loading: the draft stays, Send is off. */
    disabled?: boolean;
    projectId?: number;
    briefId?: number;
    /** Resolves true once the note is saved; false keeps the draft. */
    onSubmit: (message: string) => Promise<boolean>;
}

// The note composer (copied from the video-review CreateCommentPanel, minus
// the timestamp picker, feedback criteria and guest name). Enter inserts a
// newline; only the Send button submits.
export const BriefNoteComposer: FC<Props> = ({
    saving,
    disabled,
    projectId,
    briefId,
    onSubmit,
}) => {
    const [message, setMessage] = useState("");
    const [picked, setPicked] = useState<TaggableUser[]>([]);

    const stored = serializeMentions(message.trim(), picked);
    const isSendDisabled =
        saving || disabled || !message.trim() || stored.length > BRIEF_NOTE_MAX_LENGTH;

    const submitNote = async () => {
        if (isSendDisabled) return;
        // On failure the hook has toasted; the draft stays for a retry.
        if (await onSubmit(stored)) {
            setMessage("");
            setPicked([]);
        }
    };

    return (
        <Box className="flex flex-col gap-3">
            <MentionTextarea
                value={message}
                onChange={setMessage}
                usersUrl={
                    projectId && briefId
                        ? briefNoteTaggableUsers(projectId, briefId)
                        : null
                }
                onMentionPicked={(user) =>
                    setPicked((prev) =>
                        prev.some((p) => p.userid === user.userid) ? prev : [...prev, user],
                    )
                }
                maxLength={BRIEF_NOTE_MAX_LENGTH}
                ariaLabel="New note"
                className="bg-surface-2 text-text-1 p-3 rounded-xl resize-none w-full outline-0"
                placeholder="Add a note about this brief, @ to tag someone"
            />
            <Box className="flex items-center justify-end w-full">
                <Button
                    disabled={isSendDisabled}
                    className={classNames("!px-3 h-[40px] !flex !items-center !min-w-fit", {
                        "!bg-tint": isSendDisabled
                    })}
                    onClick={submitNote}
                >
                    {saving ? "Sending..." : "Send"}
                </Button>
            </Box>
        </Box>
    );
};
